// src/services/tmdbService.js

import axios from 'axios';

// ============================================================
// TMDB CONFIGURATION
// ============================================================

const TMDB_API_KEY = process.env.REACT_APP_TMDB_API_KEY;
const BASE_URL = 'https://api.themoviedb.org/3';

const tmdbService = axios.create({
  baseURL: BASE_URL,
  params: {
    api_key: TMDB_API_KEY,
  },
});

// ============================================================
// MOVIES
// ============================================================

export const getTrendingMovies = async (page = 1) => {
  try {
    const response = await tmdbService.get('/movie/popular', {
      params: {
        include_adult: false,
        page,
      },
    });

    const movies = response.data?.results || [];

    const moviesWithDetails = await Promise.all(
      movies.map(async (movie) => {
        try {
          const details = await tmdbService.get(`/movie/${movie.id}`, {
            params: {
              include_adult: false,
              append_to_response: 'images,videos',
            },
          });

          return details.data;
        } catch (error) {
          console.error(
            `Failed to fetch details for movie ${movie.id}:`,
            error
          );

          return movie;
        }
      })
    );

    return moviesWithDetails;
  } catch (error) {
    console.error('Error fetching trending movies:', error);
    throw error;
  }
};

export const getNowPlayingMovies = async () => {
  try {
    const response = await tmdbService.get('/movie/now_playing', {
      params: {
        include_adult: false,
        append_to_response: 'videos,images',
      },
    });

    return response.data?.results || [];
  } catch (error) {
    console.error('Error fetching now playing movies:', error);
    throw error;
  }
};

export const getPopularMovies = async (page = 1) => {
  try {
    const response = await tmdbService.get('/movie/top_rated', {
      params: {
        include_adult: false,
        page,
        append_to_response: 'videos,images',
      },
    });

    return response.data?.results || [];
  } catch (error) {
    console.error('Error fetching popular movies:', error);
    throw error;
  }
};

// ============================================================
// RECENTLY RELEASED MOVIES
// HANYA FILM YANG SUDAH RILIS DIGITAL
// ============================================================

export const getRecentlyReleasedMovies = async (
  page = 1
) => {
  try {
    const today = new Date()
      .toISOString()
      .split('T')[0];

    const response = await tmdbService.get(
      '/discover/movie',
      {
        params: {
          include_adult: false,
          include_video: false,

          page,

          // Indonesia
          region: 'ID',

          // Hanya film yang sudah rilis DIGITAL
          // TMDB:
          // 3 = Theatrical
          // 4 = Digital
          with_release_type: 4,

          // Sudah tersedia sampai hari ini
          'release_date.lte': today,

          // Minimal ada voting
          'vote_count.gte': 1,

          // Film terbaru terlebih dahulu
          sort_by: 'release_date.desc',
        },
      }
    );

    const movies =
      response.data?.results || [];

    // Pastikan hanya film yang:
    // 1. Memiliki tanggal rilis
    // 2. Tanggal rilis <= hari ini
    const releasedMovies = movies.filter(
      (movie) => {
        if (!movie.release_date) {
          return false;
        }

        return movie.release_date <= today;
      }
    );

    // Urutkan terbaru → terlama
    return releasedMovies.sort(
      (a, b) => {
        const dateA = a.release_date
          ? new Date(
              a.release_date
            ).getTime()
          : 0;

        const dateB = b.release_date
          ? new Date(
              b.release_date
            ).getTime()
          : 0;

        return dateB - dateA;
      }
    );
  } catch (error) {
    console.error(
      'Error fetching recently released movies:',
      error
    );

    throw error;
  }
};

// ============================================================
// UPCOMING MOVIES
// ============================================================

export const getUpcomingMovies = async (page = 1) => {
  try {
    const response = await tmdbService.get('/movie/upcoming', {
      params: {
        include_adult: false,
        page,
      },
    });

    return response.data?.results || [];
  } catch (error) {
    console.error('Error fetching upcoming movies:', error);
    throw error;
  }
};

// ============================================================
// MOVIE DETAILS
// ============================================================

export const getMovieDetails = async (movieId) => {
  try {
    const response = await tmdbService.get(`/movie/${movieId}`, {
      params: {
        include_adult: false,
        append_to_response:
          'credits,videos,images',
        include_image_language: 'en-US',
      },
    });

    return response.data;
  } catch (error) {
    console.error(
      `Error fetching movie details for ${movieId}:`,
      error
    );

    throw error;
  }
};

// ============================================================
// MOVIE TRAILER
// ============================================================

export const getMovieTrailer = async (id) => {
  try {
    const response = await tmdbService.get(`/movie/${id}/videos`);

    const videos = response.data?.results || [];

    const trailer = videos.find(
      (video) =>
        video.site === 'YouTube' &&
        video.type === 'Trailer'
    );

    return trailer?.key || null;
  } catch (error) {
    console.error(
      `Error fetching movie trailer for ${id}:`,
      error
    );

    return null;
  }
};

// ============================================================
// MOVIE VIDEOS
// ============================================================

export const getMovieVideos = async (movieId) => {
  try {
    const response = await tmdbService.get(
      `/movie/${movieId}/videos`
    );

    const videos = response.data?.results || [];

    const youtubeVideos = videos.filter(
      (video) => video.site === 'YouTube'
    );

    if (youtubeVideos.length === 0) {
      return [];
    }

    // 1. Official Trailer
    const officialTrailers = youtubeVideos.filter(
      (video) =>
        video.official &&
        video.type === 'Trailer'
    );

    if (officialTrailers.length > 0) {
      return officialTrailers;
    }

    // 2. Trailer
    const trailers = youtubeVideos.filter(
      (video) => video.type === 'Trailer'
    );

    if (trailers.length > 0) {
      return trailers;
    }

    // 3. Clip
    const clips = youtubeVideos.filter(
      (video) => video.type === 'Clip'
    );

    if (clips.length > 0) {
      return clips;
    }

    return youtubeVideos;
  } catch (error) {
    console.error(
      `Error fetching videos for movie ${movieId}:`,
      error
    );

    return [];
  }
};

// ============================================================
// MOVIE RECOMMENDATIONS
// ============================================================

export const getMovieRecommendations = async (movieId) => {
  try {
    const response = await tmdbService.get(
      `/movie/${movieId}/recommendations`
    );

    return response.data;
  } catch (error) {
    throw new Error(
      `Error in getMovieRecommendations: ${error.message}`
    );
  }
};

// ============================================================
// MOVIE REVIEWS
// ============================================================

export const getMovieReviews = async (movieId) => {
  try {
    const response = await tmdbService.get(
      `/movie/${movieId}/reviews`
    );

    return response.data;
  } catch (error) {
    throw new Error(
      `Error in getMovieReviews: ${error.message}`
    );
  }
};

// ============================================================
// TV SHOWS
// ============================================================

export const getTrendingTvShows = async (page = 1) => {
  try {
    const response = await tmdbService.get('/trending/tv/day', {
      params: {
        include_adult: false,
        page,
        append_to_response: 'videos,images',
      },
    });

    return response.data?.results || [];
  } catch (error) {
    console.error(
      'Error fetching trending TV shows:',
      error
    );

    throw error;
  }
};

export const getPopularTvShows = async (page = 1) => {
  try {
    const response = await tmdbService.get('/tv/top_rated', {
      params: {
        include_adult: false,
        page,
        append_to_response: 'videos,images',
      },
    });

    return response.data?.results || [];
  } catch (error) {
    console.error(
      'Error fetching popular TV shows:',
      error
    );

    throw error;
  }
};

export const getUpcomingTvShows = async (page = 1) => {
  try {
    const response = await tmdbService.get('/tv/on_the_air', {
      params: {
        include_adult: false,
        page,
      },
    });

    return (response.data?.results || []).map((show) => ({
      ...show,
      image: show.poster_path
        ? `https://image.tmdb.org/t/p/w300${show.poster_path}`
        : null,
    }));
  } catch (error) {
    console.error(
      'Error fetching upcoming TV shows:',
      error
    );

    throw error;
  }
};

// ============================================================
// TV DETAILS
// ============================================================

export const getTvShowDetails = async (id) => {
  try {
    const response = await tmdbService.get(`/tv/${id}`, {
      params: {
        include_adult: false,
        append_to_response:
          'credits,seasons,videos,images',
        include_image_language: 'en-US',
      },
    });

    if (
      !response.data ||
      !response.data.seasons
    ) {
      throw new Error(
        'Failed to fetch TV show details'
      );
    }

    const data = response.data;

    const seasons = await Promise.all(
      data.seasons.map(async (season) => {
        try {
          const episodes = await getSeasonEpisodes(
            id,
            season.season_number
          );

          return {
            season_number:
              season.season_number,
            episodes,
          };
        } catch (error) {
          console.error(
            `Failed to fetch season ${season.season_number}:`,
            error
          );

          return {
            season_number:
              season.season_number,
            episodes: [],
          };
        }
      })
    );

    return {
      name: data.name,
      poster_path: data.poster_path,
      first_air_date: data.first_air_date,
      episode_run_time:
        data.episode_run_time,
      genres: data.genres,
      overview: data.overview,
      vote_average: data.vote_average,
      created_by: data.created_by,
      backdrop_path: data.backdrop_path,
      tagline: data.tagline,
      number_of_seasons:
        data.number_of_seasons,
      number_of_episodes:
        data.number_of_episodes,
      seasons,
      credits: data.credits,
      images: data.images,
    };
  } catch (error) {
    console.error(
      `Error fetching TV show details for ${id}:`,
      error
    );

    throw error;
  }
};

// ============================================================
// TV SEASONS / EPISODES
// ============================================================

export const getSeasonEpisodes = async (
  tvShowId,
  seasonNumber
) => {
  try {
    const response = await tmdbService.get(
      `/tv/${tvShowId}/season/${seasonNumber}`
    );

    const episodes =
      response.data?.episodes || [];

    return episodes.map((episode) => ({
      id: episode.id,
      name: episode.name,
      still_path: episode.still_path,
      image: episode.still_path
        ? `https://image.tmdb.org/t/p/original${episode.still_path}`
        : null,
    }));
  } catch (error) {
    console.error(
      `Error fetching season ${seasonNumber} episodes:`,
      error
    );

    throw error;
  }
};

// ============================================================
// TV TRAILER
// ============================================================

export const getTvTrailer = async (id) => {
  try {
    const response = await tmdbService.get(
      `/tv/${id}/videos`
    );

    const videos =
      response.data?.results || [];

    const trailer = videos.find(
      (video) =>
        video.site === 'YouTube' &&
        video.type === 'Trailer'
    );

    return trailer?.key || null;
  } catch (error) {
    console.error(
      `Error fetching TV trailer for ${id}:`,
      error
    );

    return null;
  }
};

// ============================================================
// TV RECOMMENDATIONS
// ============================================================

export const getTvRecommendations = async (tvId) => {
  try {
    const response = await tmdbService.get(
      `/tv/${tvId}/recommendations`
    );

    return response.data;
  } catch (error) {
    throw new Error(
      `Error in getTvRecommendations: ${error.message}`
    );
  }
};

// ============================================================
// TV REVIEWS
// ============================================================

export const getTvReviews = async (tvId) => {
  try {
    const response = await tmdbService.get(
      `/tv/${tvId}/reviews`
    );

    return response.data;
  } catch (error) {
    throw new Error(
      `Error in getTvReviews: ${error.message}`
    );
  }
};

// ============================================================
// GENRES
// ============================================================

export const getGenres = async () => {
  try {
    const response = await tmdbService.get(
      '/genre/movie/list'
    );

    return response.data?.genres || [];
  } catch (error) {
    console.error(
      'Error fetching movie genres:',
      error
    );

    throw error;
  }
};

export const getTvGenres = async () => {
  try {
    const response = await tmdbService.get(
      '/genre/tv/list'
    );

    return response.data?.genres || [];
  } catch (error) {
    console.error(
      'Error fetching TV genres:',
      error
    );

    throw error;
  }
};

// ============================================================
// MOVIES BY GENRE
// ============================================================

export const getMoviesByGenre = async (
  genreId,
  page = 1
) => {
  try {
    const response = await tmdbService.get(
      '/discover/movie',
      {
        params: {
          include_adult: false,
          with_genres: genreId,
          page,
        },
      }
    );

    return response.data?.results || [];
  } catch (error) {
    console.error(
      'Error fetching movies by genre:',
      error
    );

    throw error;
  }
};

// ============================================================
// TV SHOWS BY GENRE
// ============================================================

export const getTvByGenre = async (
  genreId,
  page = 1
) => {
  try {
    const response = await tmdbService.get(
      '/discover/tv',
      {
        params: {
          include_adult: false,
          with_genres: genreId,
          page,
        },
      }
    );

    return response.data?.results || [];
  } catch (error) {
    console.error(
      'Error fetching TV shows by genre:',
      error
    );

    throw error;
  }
};

// ============================================================
// ANIME MOVIES - NEWEST RELEASES
// ============================================================

export const getAnimeMovies = async (page = 1) => {
  try {
    const response = await tmdbService.get(
      '/discover/movie',
      {
        params: {
          include_adult: false,
          include_video: false,
          with_keywords: 210024,
          sort_by: 'primary_release_date.desc',
          page,
          'vote_count.gte': 1,
        },
      }
    );

    const movies =
      response.data?.results || [];

    return movies.sort((a, b) => {
      const dateA = a.release_date
        ? new Date(a.release_date).getTime()
        : 0;

      const dateB = b.release_date
        ? new Date(b.release_date).getTime()
        : 0;

      return dateB - dateA;
    });
  } catch (error) {
    console.error(
      'Error fetching anime movies:',
      error
    );

    throw error;
  }
};

// ============================================================
// ANIME TV - NEWEST RELEASES
// ============================================================

export const getAnimeTv = async (page = 1) => {
  try {
    const response = await tmdbService.get(
      '/discover/tv',
      {
        params: {
          include_adult: false,
          with_keywords: 210024,
          sort_by: 'first_air_date.desc',
          page,
          'vote_count.gte': 1,
        },
      }
    );

    const shows =
      response.data?.results || [];

    return shows.sort((a, b) => {
      const dateA = a.first_air_date
        ? new Date(a.first_air_date).getTime()
        : 0;

      const dateB = b.first_air_date
        ? new Date(b.first_air_date).getTime()
        : 0;

      return dateB - dateA;
    });
  } catch (error) {
    console.error(
      'Error fetching anime TV:',
      error
    );

    throw error;
  }
};

// ============================================================
// INDIAN MOVIES - NEWEST RELEASES
// ============================================================

export const getIndianMovies = async (page = 1) => {
  try {
    const response = await tmdbService.get(
      '/discover/movie',
      {
        params: {
          include_adult: false,
          include_video: false,

          // India
          with_origin_country: 'IN',

          // Terbaru terlebih dahulu
          sort_by: 'primary_release_date.desc',

          page,

          // Hanya film yang sudah memiliki tanggal rilis
          'primary_release_date.lte': new Date()
            .toISOString()
            .split('T')[0],
        },
      }
    );

    const movies = response.data?.results || [];

    console.log(
      'Indian Movies:',
      movies.length,
      movies
    );

    // Pastikan urutan terbaru → terlama
    return movies.sort((a, b) => {
      const dateA = a.release_date
        ? new Date(a.release_date).getTime()
        : 0;

      const dateB = b.release_date
        ? new Date(b.release_date).getTime()
        : 0;

      return dateB - dateA;
    });
  } catch (error) {
    console.error(
      'Error fetching Indian movies:',
      error.response?.data || error.message
    );

    throw error;
  }
};

// ============================================================
// INDIAN TV - NEWEST RELEASES
// ============================================================

export const getIndianTv = async (page = 1) => {
  try {
    const response = await tmdbService.get(
      '/discover/tv',
      {
        params: {
          include_adult: false,

          // India
          with_origin_country: 'IN',

          // Terbaru terlebih dahulu
          sort_by: 'first_air_date.desc',

          page,

          // Jangan tampilkan acara yang belum tayang
          'first_air_date.lte': new Date()
            .toISOString()
            .split('T')[0],
        },
      }
    );

    const shows = response.data?.results || [];

    console.log(
      'Indian TV Shows:',
      shows.length,
      shows
    );

    // Pastikan urutan terbaru → terlama
    return shows.sort((a, b) => {
      const dateA = a.first_air_date
        ? new Date(a.first_air_date).getTime()
        : 0;

      const dateB = b.first_air_date
        ? new Date(b.first_air_date).getTime()
        : 0;

      return dateB - dateA;
    });
  } catch (error) {
    console.error(
      'Error fetching Indian TV shows:',
      error.response?.data || error.message
    );

    throw error;
  }
};
// ============================================================
// INDONESIAN MOVIES - DIGITAL RELEASES
// HANYA FILM INDONESIA YANG SUDAH RILIS DIGITAL
// ============================================================

export const getIndonesianMovies = async (
  page = 1
) => {
  try {
    const today = new Date()
      .toISOString()
      .split('T')[0];

    const response = await tmdbService.get(
      '/discover/movie',
      {
        params: {
          include_adult: false,
          include_video: false,

          // Film Indonesia
          with_origin_country: 'ID',

          // Rilis untuk wilayah Indonesia
          region: 'ID',

          // 4 = Digital
          // Tidak mengambil theatrical/bioskop
          with_release_type: 4,

          // Hanya yang sudah dirilis
          'release_date.lte': today,

          // Minimal ada voting
          'vote_count.gte': 1,

          // Terbaru → terlama
          sort_by: 'release_date.desc',

          page,
        },
      }
    );

    const movies =
      response.data?.results || [];

    // Pastikan hanya film yang memiliki
    // tanggal rilis dan sudah dirilis
    const releasedMovies = movies.filter(
      (movie) => {
        if (!movie.release_date) {
          return false;
        }

        return movie.release_date <= today;
      }
    );

    // Urutkan terbaru → terlama
    return releasedMovies.sort(
      (a, b) => {
        const dateA = a.release_date
          ? new Date(
              a.release_date
            ).getTime()
          : 0;

        const dateB = b.release_date
          ? new Date(
              b.release_date
            ).getTime()
          : 0;

        return dateB - dateA;
      }
    );
  } catch (error) {
    console.error(
      'Error fetching Indonesian movies:',
      error.response?.data ||
        error.message
    );

    throw error;
  }
};
// ============================================================
// INDONESIAN TV - NEWEST RELEASES
// ============================================================

export const getIndonesianTv = async (
  page = 1
) => {
  try {
    const response = await tmdbService.get(
      '/discover/tv',
      {
        params: {
          include_adult: false,
          with_origin_country: 'ID',
          sort_by: 'first_air_date.desc',
          page,
          'vote_count.gte': 1,
        },
      }
    );

    const shows =
      response.data?.results || [];

    return shows.sort((a, b) => {
      const dateA = a.first_air_date
        ? new Date(a.first_air_date).getTime()
        : 0;

      const dateB = b.first_air_date
        ? new Date(b.first_air_date).getTime()
        : 0;

      return dateB - dateA;
    });
  } catch (error) {
    console.error(
      'Error fetching Indonesian TV:',
      error
    );

    throw error;
  }
};

// ============================================================
// TOP ACTORS
// ============================================================

export const getTopActors = async (
  page = 1
) => {
  try {
    const response = await tmdbService.get(
      '/person/popular',
      {
        params: {
          page,
        },
      }
    );

    return response.data?.results || [];
  } catch (error) {
    console.error(
      'Error fetching top actors:',
      error
    );

    throw error;
  }
};

// ============================================================
// SEARCH
// ============================================================

export const searchMedia = async (
  query
) => {
  try {
    const cleanQuery = query.trim();

    if (!cleanQuery) {
      return [];
    }

    const response = await tmdbService.get(
      '/search/multi',
      {
        params: {
          include_adult: false,
          query: cleanQuery,
        },
      }
    );

    const results =
      response.data?.results || [];

    return results.map((result) => ({
      id: result.id,
      title:
        result.title ||
        result.name ||
        'Untitled',
      poster_path:
        result.poster_path || null,
      media_type:
        result.media_type,
    }));
  } catch (error) {
    console.error(
      'Error fetching search results:',
      error
    );

    throw error;
  }
};

// ============================================================
// BANNER
// ============================================================

export const getBannerMovies = async () => {
  try {
    const response = await tmdbService.get(
      '/movie/now_playing',
      {
        params: {
          include_adult: false,
          page: 1,
        },
      }
    );

    return (
      response.data?.results || []
    ).slice(0, 6);
  } catch (error) {
    console.error(
      'Error fetching banner movies:',
      error
    );

    throw error;
  }
};

// ============================================================
// QUICK INFO
// ============================================================

export const getMovieQuickInfo = async (
  id
) => {
  try {
    const response =
      await tmdbService.get(
        `/movie/${id}`
      );

    return {
      id: response.data.id,
      title: response.data.title,
      poster_path:
        response.data.poster_path,
      media_type: 'movie',
    };
  } catch (error) {
    console.error(
      `Error fetching movie quick info for ${id}:`,
      error
    );

    throw error;
  }
};

export const getTvQuickInfo = async (
  id
) => {
  try {
    const response =
      await tmdbService.get(
        `/tv/${id}`
      );

    return {
      id: response.data.id,
      name: response.data.name,
      poster_path:
        response.data.poster_path,
      media_type: 'tv',
    };
  } catch (error) {
    console.error(
      `Error fetching TV quick info for ${id}:`,
      error
    );

    throw error;
  }
};

// ============================================================
// TRENDING MOVIE TRAILERS
// ============================================================

export const getTrendingMovieTrailers =
  async () => {
    try {
      const movies =
        await getNowPlayingMovies();

      const trailers =
        await Promise.all(
          movies.map(async (movie) => {
            const trailerKey =
              await getMovieTrailer(
                movie.id
              );

            return {
              id: movie.id,
              title: movie.title,
              trailerKey,
            };
          })
        );

      return trailers;
    } catch (error) {
      console.error(
        'Error fetching trending movie trailers:',
        error
      );

      throw error;
    }
  };

// ============================================================
// SHORTS
// ============================================================

export const getShortsData = async () => {
  try {
    const response =
      await tmdbService.get(
        '/movie/popular',
        {
          params: {
            include_adult: false,
            page: 1,
          },
        }
      );

    const popularMovies =
      response.data?.results || [];

    const moviesWithVideos =
      await Promise.all(
        popularMovies.map(
          async (movie) => {
            try {
              const videos =
                await getMovieVideos(
                  movie.id
                );

              return {
                ...movie,
                videos,
              };
            } catch (error) {
              console.error(
                `Failed to fetch videos for movie ${movie.id}:`,
                error
              );

              return {
                ...movie,
                videos: [],
              };
            }
          }
        )
      );

    return moviesWithVideos
      .filter(
        (movie) =>
          movie.videos?.length > 0 &&
          movie.backdrop_path &&
          movie.poster_path
      )
      .slice(0, 50);
  } catch (error) {
    console.error(
      'Error fetching shorts data:',
      error
    );

    throw error;
  }
};